import { useMemo } from "react";

import { useQueries } from "@tanstack/react-query";

import { entitiesApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import { LoadEntitiesRequestEnvEnum } from "src/clients/entities/api";
import {
  PropertyDefinitionOutput,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import { transformToFrontendEntity } from "src/entities/entityView/utils";
import {
  EntityResource,
  EntitySchemaProperty,
  EntitySchemaResource,
  FlatEntityResource,
  useEntitySchemas,
} from "src/entities/queries";
import { Environment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type EntityRef = {
  id: string;
  entity_type: string;
};

type EntityBatch = {
  schemaId: string;
  entityIds: string[];
};

type QueryResult = {
  schemaId: string;
  entities: EntityResource[];
};

const QUERY_STALE_TIME = 5 * 60 * 1000;
const BATCH_SIZE = 100;

const getEntityDisplayValueFromEntity = (
  entity: EntityResource,
  primaryProperty?: string,
): string => {
  const value = primaryProperty ? entity.properties?.[primaryProperty] : null;
  return typeof value === "string" && value ? value : entity._id;
};

const groupEntityRefsBySchema = (
  entityRefs: EntityRef[],
): Map<string, Set<string>> => {
  const refs = new Map<string, Set<string>>();

  entityRefs.forEach((ref) => {
    if (!ref.id || !ref.entity_type) return;

    if (!refs.has(ref.entity_type)) {
      refs.set(ref.entity_type, new Set());
    }
    refs.get(ref.entity_type)!.add(ref.id);
  });

  return refs;
};

const createEntityBatches = (
  entityReferences: Map<string, Set<string>>,
): EntityBatch[] => {
  const batches: EntityBatch[] = [];

  entityReferences.forEach((entityIds, schemaId) => {
    const idsArray = Array.from(entityIds);
    for (let i = 0; i < idsArray.length; i += BATCH_SIZE) {
      batches.push({
        schemaId,
        entityIds: idsArray.slice(i, i + BATCH_SIZE),
      });
    }
  });

  return batches;
};

type UseEntityDisplayValuesParams = {
  entityRefs: EntityRef[];
  environment: Environment;
  enabled?: boolean;
};

export const useEntityDisplayValues = ({
  entityRefs,
  environment,
  enabled = true,
}: UseEntityDisplayValuesParams) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const { data: workspace } = useCurrentWorkspace();
  const { entitiesEnabled } = useWorkspaceFeatureGates();

  const apiClient = useMemo(
    () => (workspace?.base_url ? entitiesApi(workspace.base_url) : undefined),
    [workspace?.base_url],
  );

  const shouldFetch = enabled && entitiesEnabled && entityRefs.length > 0;

  const { data: schemasData, isLoading: schemasLoading } = useEntitySchemas({
    baseUrl: workspace?.base_url || "",
    options: { enabled: shouldFetch && !!workspace?.base_url },
  });

  const schemaPrimaryProperties = useMemo(
    () =>
      new Map(
        schemasData?.entities
          ?.filter((schema) => schema._id && schema._primary_property)
          .map((schema) => [schema._id, schema._primary_property!]) || [],
      ),
    [schemasData],
  );

  const entityBatches = useMemo(
    () =>
      shouldFetch
        ? createEntityBatches(groupEntityRefsBySchema(entityRefs))
        : [],
    [entityRefs, shouldFetch],
  );

  const entityQueries = useQueries({
    queries: entityBatches.map(({ schemaId, entityIds }) => ({
      queryKey: [
        "entities",
        "loadBatch",
        wsId,
        schemaId,
        environment,
        entityIds.join(","),
      ],
      queryFn: async (): Promise<QueryResult> => {
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const response =
          await apiClient.loadEntitiesEntitiesApiV1OperationsLoadEntitiesPost({
            loadEntitiesRequest: {
              env: environment as LoadEntitiesRequestEnvEnum,
              entity_schema: schemaId,
              entity_ids: entityIds,
            },
          });

        const entitiesDict = response.data.data?.entities || {};
        const entities = Object.entries(entitiesDict).map(([id, entity]) =>
          transformToFrontendEntity({ ...entity, _id: id }),
        );

        return { schemaId, entities };
      },
      enabled: shouldFetch && !!apiClient && entityIds.length > 0,
      staleTime: QUERY_STALE_TIME,
    })),
    combine: (results) => ({
      data: results.map((result) => result.data),
      isLoading: results.some((result) => result.isLoading),
    }),
  });

  // Keep separate from `combine`, it depends on schemaPrimaryProperties which loads independently
  const displayMap = useMemo(() => {
    const map: Record<string, string> = {};

    if (!entitiesEnabled) {
      return map;
    }

    entityQueries.data?.forEach((data) => {
      if (!data) return;

      const { schemaId, entities } = data;
      const primaryProperty = schemaPrimaryProperties.get(schemaId);

      entities.forEach((entity) => {
        map[entity._id] = getEntityDisplayValueFromEntity(
          entity,
          primaryProperty,
        );
      });
    });

    return map;
  }, [entityQueries.data, schemaPrimaryProperties, entitiesEnabled]);

  const isLoading = shouldFetch && (schemasLoading || entityQueries.isLoading);

  return { displayMap, isLoading };
};

/**
 * Takes flat entities and their schema, resolves relation column entity IDs
 * to primary property display names, and returns entities with enriched _fk_pp.
 */
export const useEnrichedEntitiesWithRelationNames = ({
  entities,
  schema,
  environment,
}: {
  entities: FlatEntityResource[];
  schema: EntitySchemaResource | undefined;
  environment: Environment;
}): FlatEntityResource[] => {
  const entityRefs = useMemo(() => {
    if (!schema || entities.length === 0) return [];

    const relationProps = Object.entries(schema.properties ?? {})
      .filter(([, prop]) => {
        const p = prop as EntitySchemaProperty;
        return p._type === "relation" && Boolean(p._rel_schema);
      })
      .map(
        ([key, prop]) =>
          [key, (prop as EntitySchemaProperty)._rel_schema!] as const,
      );

    if (relationProps.length === 0) return [];

    const seen = new Set<string>();

    return entities.flatMap((entity) =>
      relationProps.flatMap(([key, relSchema]) => {
        const value = entity[key];
        const ids =
          typeof value === "string"
            ? [value]
            : Array.isArray(value)
              ? (value as unknown[]).filter(
                  (v): v is string => typeof v === "string",
                )
              : [];

        return ids
          .filter((id) => id && !seen.has(id))
          .map((id) => {
            seen.add(id);
            return { id, entity_type: relSchema };
          });
      }),
    );
  }, [schema, entities]);

  const { displayMap } = useEntityDisplayValues({
    entityRefs,
    environment,
    enabled: entityRefs.length > 0,
  });

  return useMemo(() => {
    if (!Object.keys(displayMap).length) return entities;

    const additions: Record<string, string> = {};
    for (const ref of entityRefs) {
      if (displayMap[ref.id]) {
        additions[`${ref.entity_type}.${ref.id}`] = displayMap[ref.id];
      }
    }
    if (!Object.keys(additions).length) return entities;

    return entities.map((entity) => ({
      ...entity,
      _fk_pp: { ...entity._fk_pp, ...additions },
    }));
  }, [entities, displayMap, entityRefs]);
};

export const useEventEntityDisplayMap = ({
  eventSchema,
  events,
  environment,
}: {
  eventSchema:
    | { properties: Record<string, PropertyDefinitionOutput> }
    | undefined;
  events: { payload: Record<string, unknown> }[];
  environment: Environment;
}) => {
  const entityRefs = useMemo(() => {
    if (!eventSchema || events.length === 0) return [];

    const relationKeys = Object.entries(eventSchema.properties)
      .filter(
        ([, prop]) => prop.type === PropertyDefinitionOutputTypeEnum.RELATION,
      )
      .map(([key]) => key);

    if (relationKeys.length === 0) return [];

    const seen = new Set<string>();

    return events.flatMap((event) =>
      relationKeys.flatMap((key) => {
        const cellValue = event.payload[key] as
          | { _id: string; _type: string }
          | undefined
          | null;
        if (!cellValue?._id || !cellValue?._type || seen.has(cellValue._id))
          return [];
        seen.add(cellValue._id);
        return [{ id: cellValue._id, entity_type: cellValue._type }];
      }),
    );
  }, [eventSchema, events]);

  return useEntityDisplayValues({
    entityRefs,
    environment,
    enabled: entityRefs.length > 0,
  });
};
